import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, LockKey, Plant, Tree, BatteryFull, Users, Lightning, Medal } from "@phosphor-icons/react";
import { DASHBOARD } from "@/constants/testIds";

const ICONS = { Plant, Tree, BatteryFull, Users, Lightning, Medal };

export const MilestonesTracker = ({ milestones = [] }) => {
    return (
        <section
            data-testid={DASHBOARD.milestones}
            className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10"
        >
            <div className="mb-6 flex flex-col gap-3 sm:mb-8 md:flex-row md:items-end md:justify-between">
                <div>
                    <span className="eviq-chip">Milestones · Trees planted for every step</span>
                    <h2 className="mt-4 font-editorial text-2xl leading-tight text-white sm:text-3xl md:text-4xl">
                        Your battery care, rewarded in trees.
                    </h2>
                </div>
                <p className="max-w-sm text-sm text-zinc-500">
                    Milestones unlock as you drive, refer friends, and keep your pack healthy.
                    Each one contributes to verified reforestation.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {milestones.map((m, idx) => {
                    const Icon = ICONS[m.icon] || Plant;
                    const unlocked = m.unlocked;
                    return (
                        <motion.div
                            key={m.id}
                            data-testid={DASHBOARD.milestone(m.id)}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ delay: idx * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className={`relative overflow-hidden rounded-md border p-5 ${
                                unlocked
                                    ? "border-[#a3e635]/30 bg-[#0a0f05]"
                                    : "border-[#1f1f22] bg-[#0a0a0a]"
                            }`}
                        >
                            <div className="flex items-start justify-between">
                                <div className={`flex h-11 w-11 items-center justify-center rounded-md border ${
                                    unlocked ? "border-[#a3e635]/40 bg-[#141a09]" : "border-[#1f1f22] bg-[#050505]"
                                }`}>
                                    <Icon size={20} color={unlocked ? "#a3e635" : "#52525b"} weight="fill" />
                                </div>
                                {unlocked ? (
                                    <CheckCircle size={18} color="#a3e635" weight="fill" />
                                ) : (
                                    <LockKey size={16} color="#52525b" />
                                )}
                            </div>
                            <h3 className="mt-4 font-editorial text-lg text-white">{m.title}</h3>
                            <p className="mt-1 text-sm text-zinc-500">{m.description}</p>
                            <div className="mt-4 flex items-center justify-between">
                                <span className={`font-mono-metric text-[10px] uppercase tracking-[0.24em] ${
                                    unlocked ? "text-[#a3e635]" : "text-zinc-500"
                                }`}>
                                    {unlocked ? `Reward · ${m.reward}` : `Unlocks ${m.reward}`}
                                </span>
                                {!unlocked && typeof m.progress === "number" && (
                                    <span className="font-mono-metric text-xs text-zinc-400">
                                        {m.progress}%
                                    </span>
                                )}
                            </div>
                            {!unlocked && typeof m.progress === "number" && (
                                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-[#1a1a1c]">
                                    <div
                                        className="h-full rounded-full bg-gradient-to-r from-[#a3e635] to-[#06b6d4]"
                                        style={{ width: `${m.progress}%` }}
                                    />
                                </div>
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
};

export default MilestonesTracker;
